import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useAppDispatch } from '../../app/hooks';
import { setTooltipContent, removeTooltipContent } from '../../store/tooltip/tooltip_actions';
import { KEYBOARD_EVENTS } from '../../utils/constants/commonConstants';
import {
    SOURCE_UNAVAILABLE_TOOLTIP,
    INLINE_NAME_MAX_FILES,
    getMoreFilesLabel,
    getMoreFilesAccessibleLabel,
    MULTI_FILE_LIST_CLOSE_DELAY_MS,
    MULTI_FILE_LIST_OFFSET_PX,
    MULTI_FILE_LIST_MAX_WIDTH_PX,
    MULTI_FILE_LIST_MAX_HEIGHT_PX,
    MULTI_FILE_LIST_EDGE_PADDING_PX,
} from './constants';
import { SourceFile, SourceFileTagProps, SourceFileTagDisplayState } from './types';

export interface FileListPosition {
    top: number;
    left: number;
}

// Mirrors TooltipV2.tsx's own `calculatePosition` clamp math, using this popover's CSS
// max-width/max-height as the bound instead of a measured ref (nothing is mounted yet to measure).
const clampFileListPosition = (
    left: number,
    top: number,
    width: number = MULTI_FILE_LIST_MAX_WIDTH_PX,
    height: number = MULTI_FILE_LIST_MAX_HEIGHT_PX,
): FileListPosition => {
    const maxLeft = window.innerWidth - width - MULTI_FILE_LIST_EDGE_PADDING_PX;
    const maxTop = window.innerHeight - height - MULTI_FILE_LIST_EDGE_PADDING_PX;
    return {
        left: Math.max(MULTI_FILE_LIST_EDGE_PADDING_PX, Math.min(left, Math.max(MULTI_FILE_LIST_EDGE_PADDING_PX, maxLeft))),
        top: Math.max(MULTI_FILE_LIST_EDGE_PADDING_PX, Math.min(top, Math.max(MULTI_FILE_LIST_EDGE_PADDING_PX, maxTop))),
    };
};

/**
 * State/handlers for SourceFileTag — derives which of the four cell states to render
 * for ONE column of the "Reference Documents" row (see AuditedStatementTable.tsx's
 * `referenceDocumentsRow` prop for the row scaffold this mounts into).
 *
 * Only 'failure' dispatches TooltipV2 (setTooltipContent on mouse-enter, removeTooltipContent on
 * mouse-leave and on unmount — React never fires mouseleave for an element removed while
 * hovered). The primary file relies on its own native `title` attribute instead; dispatching a
 * TooltipV2 tooltip for it too would show two tooltips for one hover.
 *
 * The "+ N more" popover is different: its rows are independently clickable, so it must be
 * reachable by Tab and operable with Enter/Space. This hook keeps it OUT of TooltipV2/Redux
 * entirely and renders it as a real DOM sibling of the trigger (see SourceFileTag.tsx),
 * positioned with `position: fixed` (computed here from the trigger's own bounding rect) purely
 * to escape the ancestor `overflow` the scrollable statement table applies. Tab order still
 * follows DOM order regardless of the fixed visual position, so this needs no portal and no
 * custom focus trap.
 */
export function useSourceFileTagLogic({ sourceFiles, status, statement, onOpen }: SourceFileTagProps) {
    const dispatch = useAppDispatch();
    const files = sourceFiles ?? [];

    // Four states: 'none' (not a tagged surface), a failed lookup, a resolved-empty period ("Not
    // Available"), and 1+ files — the first file is always shown inline, with a "+ N more"
    // disclosure for the rest. Audited and Provisional render identically here: a resolved-empty
    // period shows the em dash on both, so the row behaves the same whichever tab you are on.
    const displayState: SourceFileTagDisplayState =
        status === 'UNAVAILABLE'
            ? SourceFileTagDisplayState.FAILURE
            : status === 'OK' && files.length === 0
            ? SourceFileTagDisplayState.EMPTY
            : status === 'OK'
            ? SourceFileTagDisplayState.FILES
            : SourceFileTagDisplayState.NONE;

    const primaryFile = displayState === SourceFileTagDisplayState.FILES ? files[0] : undefined;
    const moreFiles = displayState === SourceFileTagDisplayState.FILES ? files.slice(INLINE_NAME_MAX_FILES) : [];
    const hasMoreFiles = moreFiles.length > 0;
    const moreFilesLabel = hasMoreFiles ? getMoreFilesLabel(moreFiles.length) : '';
    const moreFilesAccessibleLabel = hasMoreFiles ? getMoreFilesAccessibleLabel(moreFiles.length) : '';

    const handleOpenFile = (file: SourceFile) => onOpen(file.docId, file.fileName);

    const buildTooltipContent = (): string | null => {
        if (displayState === SourceFileTagDisplayState.FAILURE) return SOURCE_UNAVAILABLE_TOOLTIP;

        // 'files' relies on the primary file's native `title` attribute instead (see docblock
        // above); the "+ N more" popover renders its own content locally, never through TooltipV2.
        return null;
    };

    const showTooltip = (target: HTMLElement) => {
        const content = buildTooltipContent();
        if (!content) return;
        dispatch(setTooltipContent({ content, target, placement: 'bottom' }));
    };

    const handleMouseEnter = (event: MouseEvent<HTMLElement>) => showTooltip(event.currentTarget);
    const handleMouseLeave = () => removeTooltipContent(dispatch);

    // Kept current every render so the cleanup effect below can read the LATEST displayState at
    // actual unmount time without adding displayState to its own deps (which would re-fire cleanup
    // on every transition, not just true unmount).
    const displayStateRef = useRef(displayState);
    useEffect(() => {
        displayStateRef.current = displayState;
    });

    // Only fires removeTooltipContent when the LAST rendered state was 'failure' — the only state
    // that dispatches anything of its own (see buildTooltipContent above). Firing on every unmount
    // regardless of state could wipe an UNRELATED cell's live tooltip (multiple statement sections
    // can mount at once).
    useEffect(
        () => () => {
            if (displayStateRef.current === SourceFileTagDisplayState.FAILURE) removeTooltipContent(dispatch);
        },
        [dispatch],
    );

    // ── "+ N more" popover — fully local (see docblock above) ──────────────────────────────────
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [isFileListOpen, setIsFileListOpen] = useState(false);
    const [fileListPosition, setFileListPosition] = useState<FileListPosition | null>(null);
    // The mounted popover element, so its ACTUAL size can be measured (see the layout effect
    // below) rather than assuming the CSS max-width.
    const fileListRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // One-shot guard: without it, focus() below (returning focus to the trigger after Escape)
    // would immediately reopen the popover via handleTriggerFocus. Armed right before that
    // focus() call, consumed by handleTriggerFocus the instant it fires.
    const suppressTriggerFocusOpenRef = useRef(false);

    const clearScheduledClose = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    };

    // Shared "reveal" handler for every way the popover opens (hover, focus, click, Enter/Space,
    // or landing on a row) — idempotent (always opens, never toggles) so a click on an already
    // hover-open trigger can't close it out from under the pointer.
    const openFileList = () => {
        clearScheduledClose();
        // Clamp only on the closed -> open transition: every way of opening calls this again even
        // when already open (e.g. a click after the hover that opened it), and re-running the
        // clamp there would overwrite the corrected position the layout effect below computed
        // from the card's real size — the effect can't restore it since its dep (isFileListOpen)
        // hasn't changed.
        if (!isFileListOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setFileListPosition(clampFileListPosition(rect.left, rect.bottom + MULTI_FILE_LIST_OFFSET_PX));
        }
        setIsFileListOpen(true);
    };

    // Shared "may close" handler for mouse-leave/blur on the trigger or a row — debounced (mirrors
    // TooltipV2's grace window) so moving between them cancels this instead of flickering shut.
    // The open-time clamp can only assume `.Popover`'s CSS max-width/max-height (nothing is
    // mounted yet to measure) — once mounted, re-clamp against the real size so a narrow card
    // sits under its own trigger instead of staying shoved toward the worst-case bound.
    useLayoutEffect(() => {
        if (!isFileListOpen || !fileListRef.current || !triggerRef.current) return;
        const card = fileListRef.current.getBoundingClientRect();
        const trigger = triggerRef.current.getBoundingClientRect();
        const next = clampFileListPosition(trigger.left, trigger.bottom + MULTI_FILE_LIST_OFFSET_PX, card.width, card.height);
        setFileListPosition((current) => (current && current.left === next.left && current.top === next.top ? current : next));
    }, [isFileListOpen]);

    const scheduleCloseFileList = () => {
        clearScheduledClose();
        closeTimeoutRef.current = setTimeout(() => setIsFileListOpen(false), MULTI_FILE_LIST_CLOSE_DELAY_MS);
    };

    // Escape closes immediately (no grace) and returns focus to the trigger — attached to the
    // wrapper in SourceFileTag.tsx so keydown bubbling covers the trigger and every
    // row without per-row wiring.
    const handleFileListKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key !== KEYBOARD_EVENTS.ESCAPE) return;
        event.stopPropagation();
        clearScheduledClose();
        setIsFileListOpen(false);
        // Armed only when focus isn't already on the trigger: if Escape was pressed while the
        // trigger itself had focus, focus() below is a same-element no-op — no focus event fires,
        // so handleTriggerFocus never runs to consume the flag. Left armed unconditionally, it
        // would swallow the NEXT real focus event (e.g. a later Tab back onto the trigger) and
        // silently fail to reopen the popover.
        if (triggerRef.current !== document.activeElement) {
            suppressTriggerFocusOpenRef.current = true;
        }
        triggerRef.current?.focus();
    };

    // onFocus for the TRIGGER specifically (rows use plain `openFileList`) — see
    // suppressTriggerFocusOpenRef above for why this can't just be `openFileList` directly.
    const handleTriggerFocus = () => {
        if (suppressTriggerFocusOpenRef.current) {
            suppressTriggerFocusOpenRef.current = false;
            return;
        }
        openFileList();
    };

    // A real <button> already fires click on Enter/Space in a browser, but jsdom/fireEvent doesn't
    // reproduce that synthesis — this makes the same behaviour explicit and testable (same
    // pattern as ClickableValue.tsx). `preventDefault` also suppresses the native synthesis, so
    // this never double-fires `openFileList` in a real browser.
    // Curried so the caller can bind it per row inside its .map(). Lives here, not in the
    // component, so every keydown handler for this cell is in one place. A real <button> fires
    // click on Enter/Space in a browser but not under jsdom/fireEvent, hence the explicit handling.
    const handleRowKeyDown = (file: SourceFile) => (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key !== KEYBOARD_EVENTS.ENTER && event.key !== KEYBOARD_EVENTS.SPACE) return;
        event.preventDefault();
        handleOpenFile(file);
    };

    const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key !== KEYBOARD_EVENTS.ENTER && event.key !== KEYBOARD_EVENTS.SPACE) return;
        event.preventDefault();
        openFileList();
    };

    // `position: fixed` means the popover won't follow the trigger on scroll, so it closes
    // instead of drifting over unrelated content. The `scroll` event doesn't bubble, but every
    // event still runs its capturing phase from `window` down — the `true` (capture) flag below
    // is what lets this `window` listener see a scroll on the table's own scrollable container.
    useEffect(() => {
        if (!isFileListOpen) return undefined;
        const handleScroll = () => setIsFileListOpen(false);
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [isFileListOpen]);

    // Only the pending close timer needs clearing on unmount — unlike the primary-file/failure
    // path above, there is no external (Redux) state left dangling once this instance goes away.
    useEffect(() => clearScheduledClose, []);

    return {
        displayState,
        primaryFile,
        moreFiles,
        hasMoreFiles,
        moreFilesLabel,
        moreFilesAccessibleLabel,
        handleMouseEnter,
        handleMouseLeave,
        handleOpenFile,
        triggerRef,
        isFileListOpen,
        fileListPosition,
        fileListRef,
        openFileList,
        scheduleCloseFileList,
        handleFileListKeyDown,
        handleRowKeyDown,
        handleTriggerKeyDown,
        handleTriggerFocus,
    };
}
